/**
 * Helpers for player tournament registration: which seasons are "open" and which campaign to show.
 */

export function seasonRowRegistrationOpen(seasonRow, seasonsList) {
  const sid = String(seasonRow.season_id || seasonRow.id);
  const sl = seasonsList.find((s) => String(s.id) === sid);
  /** League seasons API is source of truth when this season appears there. */
  if (sl) return !!sl.is_registration_open;
  const rs = seasonRow.season_registration_start ?? seasonRow.registration_start;
  const re = seasonRow.season_registration_end ?? seasonRow.registration_end;
  if (rs && re) {
    const now = Date.now();
    const t0 = new Date(rs).getTime();
    const t1 = new Date(re).getTime();
    if (!Number.isNaN(t0) && !Number.isNaN(t1) && t0 <= now && now <= t1) return true;
  }
  return false;
}

export function registrationOpenSeasonRows(campaign, seasonsList) {
  if (!campaign?.seasons?.length) return [];
  return campaign.seasons.filter((row) => seasonRowRegistrationOpen(row, seasonsList));
}

/** Prefer campaign-flagged active season, else lowest sort_order. */
export function pickPrimaryRegistrationSeasonId(campaign, seasonsList) {
  const open = registrationOpenSeasonRows(campaign, seasonsList);
  if (!open.length) return '';
  const chosen =
    open.find((s) => s.is_active_in_campaign) ||
    [...open].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0];
  return String(chosen.season_id || chosen.id);
}

/**
 * Campaign can surface player registration when at least one linked season is in its registration window.
 * We do not require event_tournament.registration_* dates to match "today" — admins often rely on season-level
 * registration only; campaign-level flags are still useful for badges elsewhere.
 */
export function campaignEligibleForPlayerRegistration(campaign, seasonsList) {
  if (!campaign || campaign.campaign_completed) return false;
  return registrationOpenSeasonRows(campaign, seasonsList).length > 0;
}

export function filterRegistrationEligibleCampaigns(campaigns, seasonsList) {
  return (campaigns || []).filter((c) => campaignEligibleForPlayerRegistration(c, seasonsList));
}

/**
 * When no campaign has an open phase window, pick admin closed-registration notice.
 * Prefer a live (published, not completed) season with text; else any with text.
 * Returns { title, body } or null (caller falls back to i18n).
 */
export function resolveClosedRegistrationNotice(campaigns) {
  const extract = (c) => {
    if (!c) return null;
    const title = String(c.registration_closed_title || '').trim();
    const body = String(c.registration_closed_body || '').trim();
    if (!title && !body) return null;
    return { title, body };
  };
  const list = campaigns || [];
  const live = list.find((c) => !c.campaign_completed && extract(c));
  if (live) return extract(live);
  const any = list.find((c) => extract(c));
  return any ? extract(any) : null;
}

/** Query value for `/player/tournaments?auto_tenant=…` (dashboard “create team” deep-link). */
export const HELVETIC_AUTO_TENANT_QUERY = 'helvetic_padel_series';

export function isHelveticPadelSeriesTenant(tenant) {
  const name = String(tenant?.name || tenant?.tenant_name || '').trim().toLowerCase();
  const slug = String(tenant?.slug || '').trim().toLowerCase();
  return (
    name.includes('helvetic padel series') ||
    slug === 'helvetic-padel-series' ||
    slug === 'helvetic_padel_series' ||
    (slug.includes('helvetic') && slug.includes('padel') && slug.includes('series'))
  );
}

export function findHelveticPadelSeriesTenant(tenantsList) {
  return (tenantsList || []).find((t) => isHelveticPadelSeriesTenant(t)) || null;
}

export function buildHelveticLeaguesCreateTeamPath() {
  return `/player/tournaments?area=leagues&auto_tenant=${HELVETIC_AUTO_TENANT_QUERY}`;
}
